import React, { useMemo, useRef } from "react";
import { FaCalendarAlt, FaSearch, FaTags, FaTimes } from "react-icons/fa";
import SearchSelectDropdown from "../TaskManagement/SearchSelectDropdown";

const FILTER_BUTTON_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

const DROPDOWN_TRIGGER_CLASS =
  "flex min-h-[34px] w-full items-center justify-between rounded-none border-0 bg-transparent px-2 py-1 text-left text-xs font-medium text-slate-800 shadow-none focus:border-transparent focus:outline-none focus:ring-0";

const ICON_MODIFIERS = {
  category: "ep-expiry-filter-control__icon--category",
  expiry: "ep-expiry-filter-control__icon--expiry",
  provider: "ep-expiry-filter-control__icon--provider",
};

function FilterChip({ category, label, onClear }) {
  return (
    <span className="ep-expiry-filter-chip">
      <span className="ep-expiry-filter-chip__category">{category}</span>
      <span className="ep-expiry-filter-chip__label">{label}</span>
      <button
        type="button"
        onClick={onClear}
        className="ep-expiry-filter-chip__clear"
        style={FILTER_BUTTON_RESET}
        aria-label={`Remove ${label}`}
      >
        <FaTimes size={7} />
      </button>
    </span>
  );
}

function FilterField({
  label,
  children,
  icon: Icon,
  tone = "category",
  className = "",
  controlRef,
}) {
  return (
    <div className={`ep-expiry-filter-field ${className}`.trim()}>
      <span className="ep-expiry-filter-label">{label}</span>
      <div className="ep-expiry-filter-control" ref={controlRef}>
        <div
          className={`ep-expiry-filter-control__icon ${ICON_MODIFIERS[tone] || ""}`}
          aria-hidden
        >
          {Icon ? <Icon size={13} /> : null}
        </div>
        <div className="ep-expiry-filter-control__body">{children}</div>
      </div>
    </div>
  );
}

export default function ExpiringProvidersFilterBar({
  categoryOptions,
  expiryOptions,
  selectedCategory,
  selectedExpiry,
  providerNameDraft,
  appliedProviderName,
  onCategoryChange,
  onExpiryChange,
  onProviderNameChange,
  onClearCategory,
  onClearExpiry,
  onClearProvider,
  onResetAll,
}) {
  const categoryBoundaryRef = useRef(null);
  const expiryBoundaryRef = useRef(null);

  const dropdownProps = {
    allowDeselect: true,
    clearable: false,
    hideSelectionInTrigger: true,
    openOnHover: true,
    usePortal: true,
    inputClass: DROPDOWN_TRIGGER_CLASS,
    menuClassName: "ep-expiry-filter-menu z-[200]",
  };

  const categorySelectOptions = useMemo(
    () => categoryOptions.filter((option) => option.value !== ""),
    [categoryOptions],
  );

  const expirySelectOptions = useMemo(
    () => expiryOptions.filter((option) => option.value !== "ALL"),
    [expiryOptions],
  );

  const categoryLabel = useMemo(
    () =>
      categoryOptions.find((option) => option.value === selectedCategory)?.label ||
      "",
    [categoryOptions, selectedCategory],
  );

  const expiryLabel = useMemo(
    () =>
      expiryOptions.find((option) => option.value === selectedExpiry)?.label || "",
    [expiryOptions, selectedExpiry],
  );

  const chips = useMemo(() => {
    const list = [];
    if (selectedCategory) {
      list.push({
        key: "category",
        category: "Category",
        label: categoryLabel,
        onClear: onClearCategory,
      });
    }
    if (selectedExpiry && selectedExpiry !== "ALL") {
      list.push({
        key: "expiry",
        category: "Expiry",
        label: expiryLabel,
        onClear: onClearExpiry,
      });
    }
    if (appliedProviderName.trim()) {
      list.push({
        key: "provider",
        category: "Provider",
        label: appliedProviderName.trim(),
        onClear: onClearProvider,
      });
    }
    return list;
  }, [
    selectedCategory,
    selectedExpiry,
    categoryLabel,
    expiryLabel,
    appliedProviderName,
    onClearCategory,
    onClearExpiry,
    onClearProvider,
  ]);

  const providerCommitted =
    Boolean(appliedProviderName.trim()) && providerNameDraft === appliedProviderName;
  const providerInputValue = providerCommitted ? "" : providerNameDraft;

  return (
    <div className="ep-expiry-filters-card">
      <div className="ep-expiry-filter-grid">
        <FilterField
          label="Category"
          icon={FaTags}
          tone="category"
          controlRef={categoryBoundaryRef}
        >
          <SearchSelectDropdown
            {...dropdownProps}
            hoverBoundaryRef={categoryBoundaryRef}
            value={selectedCategory}
            onChange={(value) => onCategoryChange(value || "")}
            options={categorySelectOptions}
            placeholder="All categories"
            searchPlaceholder="Search categories..."
            emptyLabel="No categories"
          />
        </FilterField>

        <FilterField
          label="Expiry window"
          icon={FaCalendarAlt}
          tone="expiry"
          controlRef={expiryBoundaryRef}
        >
          <SearchSelectDropdown
            {...dropdownProps}
            hoverBoundaryRef={expiryBoundaryRef}
            value={selectedExpiry === "ALL" ? "" : selectedExpiry}
            onChange={(value) => onExpiryChange(value || "ALL")}
            options={expirySelectOptions}
            placeholder="All providers"
            searchPlaceholder="Search expiry ranges..."
            emptyLabel="No expiry ranges"
          />
        </FilterField>

        <FilterField
          label="Provider"
          icon={FaSearch}
          tone="provider"
          className="ep-expiry-filter-field--provider"
        >
          <input
            type="text"
            value={providerInputValue}
            onChange={(e) => onProviderNameChange(e.target.value)}
            placeholder="Name or provider ID"
            className="ep-expiry-filter-text-input"
            autoComplete="off"
          />
        </FilterField>
      </div>

      {chips.length > 0 ? (
        <div className="ep-expiry-filters-chips-row">
          <div className="ep-expiry-filters-chips">
            {chips.map((chip) => (
              <FilterChip
                key={chip.key}
                category={chip.category}
                label={chip.label}
                onClear={chip.onClear}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onResetAll}
            className="ep-expiry-filters-reset ep-expiry-filters-reset--inline is-active"
            style={FILTER_BUTTON_RESET}
          >
            <FaTimes size={8} />
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}
